import React, { useState, useEffect } from 'react';
import { getCategoryAnalytics, getLocationStats } from '../../services/api';
import {
  FiBarChart2,
  FiTrendingUp,
  FiTrendingDown,
  FiDollarSign,
  FiUsers,
  FiMapPin,
  FiCalendar,
  FiFilter,
  FiRefreshCw,
  FiEye,
  FiDownload,
  FiAlertCircle,
  FiCheckCircle,
  FiGrid,
  FiLayers,
  FiPackage,
  FiShoppingBag,
  FiActivity,
  FiGlobe,
  FiTarget,
  FiPercent,
  FiArrowUp,
  FiArrowDown,
  FiStar,
  FiAward
} from 'react-icons/fi';

function CategoryAnalytics({ admin }) {
  const [analytics, setAnalytics] = useState(null);
  const [locationStats, setLocationStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [dateRange, setDateRange] = useState({
    date_from: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    date_to: new Date().toISOString().split('T')[0]
  });
  const [activeView, setActiveView] = useState('overview'); // overview, performance, locations
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError('');
      const [analyticsResponse, locationResponse] = await Promise.all([
        getCategoryAnalytics(dateRange),
        getLocationStats()
      ]);

      if (analyticsResponse.success && locationResponse.success) {
        setAnalytics(analyticsResponse.data);
        setLocationStats(locationResponse.data);
      } else {
        throw new Error('Failed to load analytics data');
      }
    } catch (err) {
      setError('Failed to load analytics: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    try {
      setExporting(true);
      // In a real app, this would be an API call
      setTimeout(() => {
        setSuccessMessage(`Analytics exported as ${format.toUpperCase()} successfully!`);
        setTimeout(() => setSuccessMessage(''), 3000);
        setExporting(false);
      }, 1000);
    } catch (err) {
      setError('Failed to export analytics: ' + err.message);
      setExporting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatPercentage = (value) => {
    return `${Math.round(value || 0)}%`;
  };

  const getTrendIndicator = (current, previous) => {
    if (!previous) return { icon: null, color: 'text-gray-400' };
    const trend = ((current - previous) / previous) * 100;
    if (trend > 0) {
      return { 
        icon: <FiTrendingUp className="inline ml-1" />, 
        color: 'text-green-400',
        value: `+${trend.toFixed(1)}%`
      };
    } else if (trend < 0) {
      return { 
        icon: <FiTrendingDown className="inline ml-1" />, 
        color: 'text-red-400',
        value: `${trend.toFixed(1)}%`
      };
    }
    return { icon: null, color: 'text-gray-400', value: '0%' };
  };

  // Loading Skeleton
  if (loading && !analytics) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="animate-pulse">
          {/* Header Skeleton */}
          <div className="mb-6">
            <div className="h-8 bg-gray-700 rounded w-1/4 mb-2"></div>
            <div className="h-4 bg-gray-700 rounded w-1/2"></div>
          </div>

          {/* Stats Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-gray-800 rounded-xl border border-gray-700 p-5">
                <div className="h-4 bg-gray-700 rounded w-24 mb-3"></div>
                <div className="h-8 bg-gray-700 rounded w-16"></div>
              </div>
            ))}
          </div>

          {/* Content Skeleton */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-4 bg-gray-700 rounded w-full"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="text-center py-12">
          <div className="text-gray-600 text-6xl mb-4">📊</div>
          <h3 className="text-xl font-medium text-white mb-2">Analytics Unavailable</h3>
          <p className="text-gray-400 mb-6">Failed to load analytics data. Please try again.</p>
          <button
            onClick={fetchAnalytics}
            className="px-6 py-2.5 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium flex items-center mx-auto"
          >
            <FiRefreshCw className="mr-2" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { overview, top_categories, growth_metrics, performance } = analytics;

  return (
    <div className="min-h-screen bg-gray-900 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">
              <span className="text-[#ff660e]">Category</span> Analytics
            </h1>
            <p className="text-gray-400 mt-1">Advanced insights into category performance and trends</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => handleExport('csv')}
              disabled={exporting}
              className="px-4 py-2.5 bg-gray-800 text-white border border-gray-700 rounded-lg hover:bg-gray-700 hover:border-green-500 transition-all duration-200 font-medium flex items-center hover:scale-[1.02] disabled:opacity-50"
            >
              <FiDownload className="mr-2" />
              Export CSV
            </button>
            <button
              onClick={() => handleExport('pdf')}
              disabled={exporting}
              className="px-4 py-2.5 bg-gray-800 text-white border border-gray-700 rounded-lg hover:bg-gray-700 hover:border-blue-500 transition-all duration-200 font-medium flex items-center hover:scale-[1.02] disabled:opacity-50"
            >
              <FiDownload className="mr-2" />
              Export PDF
            </button>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-900/20 border border-green-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiCheckCircle className="text-green-400 text-xl" />
            <p className="text-green-300 font-medium">{successMessage}</p>
          </div>
          <button 
            onClick={() => setSuccessMessage('')}
            className="text-green-400 hover:text-green-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-900/20 border border-red-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiAlertCircle className="text-red-400 text-xl" />
            <p className="text-red-300">{error}</p>
          </div>
          <button 
            onClick={() => setError('')}
            className="text-red-400 hover:text-red-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Date Range Filter */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center space-x-2">
            <FiCalendar className="text-[#ff660e]" />
            <span className="text-sm font-medium text-gray-300">Date Range</span>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <div className="flex items-center space-x-2">
              <input
                type="date"
                className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent text-white"
                value={dateRange.date_from}
                onChange={(e) => setDateRange({...dateRange, date_from: e.target.value})}
              />
              <span className="text-gray-400">to</span>
              <input
                type="date"
                className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent text-white"
                value={dateRange.date_to}
                onChange={(e) => setDateRange({...dateRange, date_to: e.target.value})}
              />
            </div>
            <button
              onClick={fetchAnalytics}
              className="px-4 py-2 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium flex items-center"
            >
              <FiRefreshCw className="mr-2" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* View Toggle */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-2 mb-6">
        <div className="flex space-x-1">
          {[
            { id: 'overview', label: 'Overview', icon: <FiBarChart2 /> },
            { id: 'performance', label: 'Performance', icon: <FiTrendingUp /> },
            { id: 'locations', label: 'Locations', icon: <FiMapPin /> }
          ].map((view) => (
            <button
              key={view.id}
              onClick={() => setActiveView(view.id)}
              className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center justify-center ${
                activeView === view.id
                  ? 'bg-[#ff660e] text-white shadow-lg'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white'
              }`}
            >
              {view.icon}
              <span className="ml-2">{view.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Overview Cards */}
      {(activeView === 'overview' || activeView === 'performance') && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* Total Categories Card */}
            <div className="bg-gradient-to-br from-blue-900/30 to-blue-800/20 rounded-xl border border-blue-700/30 p-5 hover:border-blue-600/50 transition-colors group">
              <div className="flex items-center justify-between mb-4">
                <div className="p-2.5 bg-blue-900/40 rounded-lg group-hover:scale-110 transition-transform">
                  <FiLayers className="text-blue-400 text-xl" />
                </div>
                <span className="text-xs text-blue-300 bg-blue-900/30 px-2 py-1 rounded">
                  {overview.main_categories} main
                </span>
              </div>
              <div className="text-2xl font-bold text-white group-hover:text-blue-300 transition-colors">
                {overview.total_categories}
              </div>
              <div className="text-sm text-gray-400 mt-1">Total Categories</div>
              <div className="flex items-center text-xs text-gray-500 mt-2">
                <span className="flex-1">{overview.subcategories} subcategories</span>
                {growth_metrics?.category_growth && (
                  <span className={`${getTrendIndicator(growth_metrics.category_growth.current, growth_metrics.category_growth.previous).color}`}>
                    {getTrendIndicator(growth_metrics.category_growth.current, growth_metrics.category_growth.previous).value}
                  </span>
                )}
              </div>
            </div>

            {/* Active Listings Card */}
            <div className="bg-gradient-to-br from-green-900/30 to-green-800/20 rounded-xl border border-green-700/30 p-5 hover:border-green-600/50 transition-colors group">
              <div className="flex items-center justify-between mb-4">
                <div className="p-2.5 bg-green-900/40 rounded-lg group-hover:scale-110 transition-transform">
                  <FiPackage className="text-green-400 text-xl" />
                </div>
                <span className="text-xs text-green-300 bg-green-900/30 px-2 py-1 rounded">
                  {overview.categories_with_listings} active
                </span>
              </div>
              <div className="text-2xl font-bold text-white group-hover:text-green-300 transition-colors">
                {overview.total_active_listings}
              </div>
              <div className="text-sm text-gray-400 mt-1">Active Listings</div>
              <div className="flex items-center text-xs text-gray-500 mt-2">
                <span className="flex-1">Across all categories</span>
                {growth_metrics?.listing_growth && (
                  <span className={`${getTrendIndicator(growth_metrics.listing_growth.current, growth_metrics.listing_growth.previous).color}`}>
                    {getTrendIndicator(growth_metrics.listing_growth.current, growth_metrics.listing_growth.previous).value}
                  </span>
                )}
              </div>
            </div>

            {/* Revenue Card */}
            <div className="bg-gradient-to-br from-purple-900/30 to-purple-800/20 rounded-xl border border-purple-700/30 p-5 hover:border-purple-600/50 transition-colors group">
              <div className="flex items-center justify-between mb-4">
                <div className="p-2.5 bg-purple-900/40 rounded-lg group-hover:scale-110 transition-transform">
                  <FiDollarSign className="text-purple-400 text-xl" />
                </div>
                <span className="text-xs text-purple-300 bg-purple-900/30 px-2 py-1 rounded">
                  Avg price
                </span>
              </div>
              <div className="text-2xl font-bold text-white group-hover:text-purple-300 transition-colors">
                {formatCurrency(top_categories?.[0]?.avg_price || 0)}
              </div>
              <div className="text-sm text-gray-400 mt-1">Top Category Revenue</div>
              <div className="flex items-center text-xs text-gray-500 mt-2">
                <span className="flex-1">{top_categories?.[0]?.name || 'N/A'}</span>
                {growth_metrics?.revenue_growth && (
                  <span className={`${getTrendIndicator(growth_metrics.revenue_growth.current, growth_metrics.revenue_growth.previous).color}`}>
                    {getTrendIndicator(growth_metrics.revenue_growth.current, growth_metrics.revenue_growth.previous).value}
                  </span>
                )}
              </div>
            </div>

            {/* Conversion Card */}
            <div className="bg-gradient-to-br from-amber-900/30 to-amber-800/20 rounded-xl border border-amber-700/30 p-5 hover:border-amber-600/50 transition-colors group">
              <div className="flex items-center justify-between mb-4">
                <div className="p-2.5 bg-amber-900/40 rounded-lg group-hover:scale-110 transition-transform">
                  <FiPercent className="text-amber-400 text-xl" />
                </div>
                <span className="text-xs text-amber-300 bg-amber-900/30 px-2 py-1 rounded">
                  Top performer
                </span>
              </div>
              <div className="text-2xl font-bold text-white group-hover:text-amber-300 transition-colors">
                {formatPercentage(performance?.[0]?.conversion_rate || 0)}
              </div>
              <div className="text-sm text-gray-400 mt-1">Conversion Rate</div>
              <div className="flex items-center text-xs text-gray-500 mt-2">
                <span className="flex-1">{performance?.[0]?.name || 'N/A'}</span>
                {growth_metrics?.conversion_growth && (
                  <span className={`${getTrendIndicator(growth_metrics.conversion_growth.current, growth_metrics.conversion_growth.previous).color}`}>
                    {getTrendIndicator(growth_metrics.conversion_growth.current, growth_metrics.conversion_growth.previous).value}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Top Categories Section */}
          {activeView === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              {/* Top Categories */}
              <div className="lg:col-span-2 bg-gray-800 rounded-xl border border-gray-700 p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center">
                    <FiAward className="text-[#ff660e] mr-2" />
                    <h3 className="text-lg font-medium text-white">Top Performing Categories</h3>
                  </div>
                  <span className="text-sm text-gray-400">
                    {top_categories?.length || 0} categories
                  </span>
                </div>
                <div className="space-y-4">
                  {top_categories?.map((category, index) => {
                    const trend = getTrendIndicator(category.growth_rate || 0, 0);
                    return (
                      <div key={category.category_id} className="bg-gray-700/30 rounded-xl border border-gray-600 p-4 hover:border-gray-500 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2">
                              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: category.background_color }}>
                                <span style={{ color: category.icon_color }}>{category.icon_name}</span>
                              </div>
                              <div>
                                <div className="flex items-center space-x-2">
                                  <span className="text-lg font-medium text-white">{category.name}</span>
                                  {index < 3 && (
                                    <span className={`text-xs px-2 py-1 rounded-full ${
                                      index === 0 ? 'bg-yellow-900/30 text-yellow-400' :
                                      index === 1 ? 'bg-gray-700 text-gray-400' :
                                      'bg-amber-900/30 text-amber-400'
                                    }`}>
                                      #{index + 1}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center space-x-3 text-sm text-gray-400 mt-1">
                                  <span>{category.total_listings} listings</span>
                                  <span>•</span>
                                  <span>{formatCurrency(category.avg_price)} avg</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className={`text-lg font-bold ${trend.color}`}>
                              {category.growth_rate > 0 ? '+' : ''}{category.growth_rate || 0}%
                              {trend.icon}
                            </div>
                            <div className="text-sm text-gray-400">Growth</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Performance Metrics */}
              <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
                <div className="flex items-center mb-6">
                  <FiActivity className="text-[#ff660e] mr-2" />
                  <h3 className="text-lg font-medium text-white">Key Metrics</h3>
                </div>
                <div className="space-y-4">
                  {[
                    { label: 'Category Depth', value: overview.max_depth || 2, icon: <FiLayers />, color: 'text-blue-400' },
                    { label: 'Avg Subcategories', value: Math.round(overview.subcategories / overview.total_categories) || 2, icon: <FiGrid />, color: 'text-green-400' },
                    { label: 'Active Ratio', value: `${Math.round((overview.categories_with_listings / overview.total_categories) * 100)}%`, icon: <FiPercent />, color: 'text-purple-400' },
                    { label: 'Top Seller Count', value: performance?.[0]?.unique_sellers || 0, icon: <FiUsers />, color: 'text-amber-400' }
                  ].map((metric, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-700/30 rounded-lg">
                      <div className="flex items-center">
                        <div className={`p-2 rounded-lg bg-gray-700 mr-3 ${metric.color}`}>
                          {metric.icon}
                        </div>
                        <span className="text-sm text-gray-300">{metric.label}</span>
                      </div>
                      <span className={`text-lg font-bold ${metric.color}`}>
                        {metric.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Performance Table */}
          {activeView === 'performance' && (
            <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden mb-6">
              <div className="p-6 border-b border-gray-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <FiTrendingUp className="text-[#ff660e] mr-2" />
                    <h3 className="text-lg font-medium text-white">Detailed Performance</h3>
                  </div>
                  <span className="text-sm text-gray-400">
                    {performance?.length || 0} categories tracked
                  </span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-700">
                  <thead className="bg-gray-900">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Category
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Listings
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Sales
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Revenue
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Views
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Conversion
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {performance?.map((category) => (
                      <tr key={category.category_id} className="hover:bg-gray-750 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: category.background_color }}>
                              <span style={{ color: category.icon_color }}>{category.icon_name}</span>
                            </div>
                            <div>
                              <div className="text-sm font-semibold text-white">{category.name}</div>
                              <div className="text-xs text-gray-500">{category.unique_sellers || 0} sellers</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-white font-medium">{category.total_listings}</div>
                          <div className="text-xs text-gray-500">{category.active_listings || 0} active</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-white font-medium">{category.completed_orders || 0}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-green-400 font-bold">{formatCurrency(category.total_sales)}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-white font-medium">{category.total_views || 0}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                            category.conversion_rate > 20 
                              ? 'bg-green-900/30 text-green-400 border border-green-800/50'
                              : category.conversion_rate > 10
                              ? 'bg-yellow-900/30 text-yellow-400 border border-yellow-800/50'
                              : 'bg-red-900/30 text-red-400 border border-red-800/50'
                          }`}>
                            {category.conversion_rate}%
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Location Analytics */}
      {activeView === 'locations' && locationStats && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* State Coverage */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
            <div className="flex items-center mb-6">
              <FiGlobe className="text-blue-400 mr-2" />
              <h3 className="text-lg font-medium text-white">State Coverage</h3>
            </div>
            <div className="text-center mb-6">
              <div className="text-4xl font-bold text-white mb-2">{locationStats?.states?.total_states || 0}</div>
              <div className="text-sm text-gray-400">Total States</div>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-gray-700/30 rounded-lg">
                <span className="text-sm text-gray-300">Users with State</span>
                <span className="text-sm font-medium text-white">{locationStats?.states?.users_with_state || 0}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-700/30 rounded-lg">
                <span className="text-sm text-gray-300">Active States</span>
                <span className="text-sm font-medium text-green-400">{locationStats?.states?.active_states || 0}</span>
              </div>
            </div>
          </div>

          {/* LGA Statistics */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
            <div className="flex items-center mb-6">
              <FiMapPin className="text-green-400 mr-2" />
              <h3 className="text-lg font-medium text-white">LGA Coverage</h3>
            </div>
            <div className="text-center mb-6">
              <div className="text-4xl font-bold text-white mb-2">{locationStats?.lgas?.total_lgas || 0}</div>
              <div className="text-sm text-gray-400">Total LGAs</div>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-gray-700/30 rounded-lg">
                <span className="text-sm text-gray-300">Avg LCDAs per LGA</span>
                <span className="text-sm font-medium text-white">
                  {Math.round(locationStats?.lgas?.avg_lcdas_per_lga || 0)}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-700/30 rounded-lg">
                <span className="text-sm text-gray-300">Highest Coverage</span>
                <span className="text-sm font-medium text-green-400">
                  {locationStats?.lgas?.state_with_most_lgas || 'N/A'}
                </span>
              </div>
            </div>
          </div>

          {/* LCDA Statistics */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
            <div className="flex items-center mb-6">
              <FiTarget className="text-purple-400 mr-2" />
              <h3 className="text-lg font-medium text-white">LCDA Coverage</h3>
            </div>
            <div className="text-center mb-6">
              <div className="text-4xl font-bold text-white mb-2">{locationStats?.lcdas?.total_lcdas || 0}</div>
              <div className="text-sm text-gray-400">Total LCDAs</div>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-gray-700/30 rounded-lg">
                <span className="text-sm text-gray-300">Users with LCDA</span>
                <span className="text-sm font-medium text-white">{locationStats?.lcdas?.users_with_lcda || 0}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-700/30 rounded-lg">
                <span className="text-sm text-gray-300">Coverage Rate</span>
                <span className="text-sm font-medium text-green-400">
                  {Math.round((locationStats?.lcdas?.users_with_lcda / (locationStats?.states?.users_with_state || 1)) * 100)}%
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Growth Trends Chart Placeholder */}
      {(activeView === 'overview' || activeView === 'performance') && (
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 mt-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <FiTrendingUp className="text-[#ff660e] mr-2" />
              <h3 className="text-lg font-medium text-white">Growth Trends</h3>
            </div>
            <span className="text-sm text-gray-400">Last 30 days</span>
          </div>
          <div className="bg-gray-900 rounded-lg p-6 h-64 flex items-center justify-center">
            <div className="text-center">
              <div className="text-gray-600 text-6xl mb-4">📈</div>
              <p className="text-gray-400">Interactive growth chart would appear here</p>
              <p className="text-sm text-gray-500 mt-2">Showing trends across categories</p>
            </div>
          </div>
        </div>
      )}

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default CategoryAnalytics;